import {
  makeStyles,
  mergeClasses,
  Skeleton,
  SkeletonItem,
  tokens,
} from "@fluentui/react-components";
import { useTranslation } from "react-i18next";

const useClasses = makeStyles({
  wrapper: {
    gridColumn: "1 / -1",
    display: "grid",
    gridTemplateColumns: "fit-content(800px) minmax(0, 1fr)",
    columnGap: tokens.spacingHorizontalXL,
    rowGap: tokens.spacingVerticalL,
    alignItems: "start",
    width: "100%",
    "@media (max-width: 900px)": {
      gridTemplateColumns: "minmax(0, 1fr)",
    },
  },
  leftColumn: {
    display: "flex",
    flexDirection: "column",
    width: "fit-content",
    maxWidth: "900px",
    minWidth: "700px",
    minHeight: "100%",
  },
  rightColumn: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
    minWidth: 0,
    width: "100%",
  },
  title: {
    marginBottom: tokens.spacingVerticalM,
  },
  contentBlock: {
    marginBottom: tokens.spacingVerticalS,
  },
  fieldBlock: {
    marginTop: tokens.spacingVerticalL,
    marginBottom: tokens.spacingVerticalS,
  },
  buttons: {
    display: "flex",
    gap: "10px",
    marginTop: tokens.spacingVerticalL,
    flexWrap: "wrap",
  },
  button: {
    width: "120px",
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  sectionHeader: {
    width: "200px",
  },
  pillRow: {
    display: "flex",
    gap: tokens.spacingHorizontalXS,
    flexWrap: "wrap",
    marginBottom: "12px",
  },
  pill: {
    width: "90px",
    borderRadius: tokens.borderRadiusLarge,
  },
  card: {
    width: "100%",
    height: "180px",
  },
  largeCard: {
    height: "220px",
  },
  smallField: {
    width: "180px",
  },
});

export const ActivityOverviewSkeleton = () => {
  const classes = useClasses();
  const { t } = useTranslation();

  return (
    <div className={classes.wrapper}>
      <div className={classes.leftColumn}>
        <Skeleton aria-label={t("loading")}>
          <SkeletonItem className={classes.title} size={32} shape="rectangle" />

          <SkeletonItem className={classes.contentBlock} size={16} />
          <SkeletonItem className={classes.contentBlock} size={16} />
          <SkeletonItem className={classes.contentBlock} size={16} />
          <SkeletonItem className={classes.contentBlock} size={16} />
          <SkeletonItem className={classes.contentBlock} size={16} />
          <SkeletonItem className={classes.contentBlock} size={16} />

          <SkeletonItem className={classes.contentBlock} size={16} />
          <SkeletonItem className={classes.contentBlock} size={16} />
          <SkeletonItem className={classes.contentBlock} size={16} />
          <SkeletonItem className={classes.contentBlock} size={16} />
          <SkeletonItem className={classes.contentBlock} size={16} />
          <SkeletonItem className={classes.contentBlock} size={16} />

          <SkeletonItem className={classes.contentBlock} size={16} />
          <SkeletonItem className={classes.contentBlock} size={16} />
          <SkeletonItem className={classes.contentBlock} size={16} />
          <SkeletonItem className={classes.contentBlock} size={16} />
          <SkeletonItem className={classes.contentBlock} size={16} />
          <SkeletonItem className={classes.contentBlock} size={16} />

          <div className={classes.buttons}>
            <SkeletonItem
              className={classes.button}
              size={36}
              shape="rectangle"
            />
            <SkeletonItem
              className={classes.button}
              size={36}
              shape="rectangle"
            />
            <SkeletonItem
              className={classes.button}
              size={36}
              shape="rectangle"
            />
          </div>
        </Skeleton>
      </div>

      <div className={classes.rightColumn}>
        <Skeleton aria-label={t("loading")}>
          <div className={classes.section}>
            <SkeletonItem
              className={classes.sectionHeader}
              size={24}
              shape="rectangle"
            />
            <SkeletonItem className={classes.fieldBlock} size={20} />
            <div className={classes.pillRow}>
              <SkeletonItem
                className={classes.pill}
                size={24}
                shape="rectangle"
              />
              <SkeletonItem
                className={classes.pill}
                size={24}
                shape="rectangle"
              />
              <SkeletonItem
                className={classes.pill}
                size={24}
                shape="rectangle"
              />
            </div>
            <SkeletonItem className={classes.fieldBlock} size={20} />
            <div className={classes.pillRow}>
              <SkeletonItem
                className={classes.pill}
                size={24}
                shape="rectangle"
              />
              <SkeletonItem
                className={classes.pill}
                size={24}
                shape="rectangle"
              />
            </div>
          </div>

          <div className={classes.section}>
            <SkeletonItem
              className={classes.sectionHeader}
              size={24}
              shape="rectangle"
            />
            <SkeletonItem className={classes.fieldBlock} size={20} />
            <div className={classes.pillRow}>
              <SkeletonItem
                className={classes.pill}
                size={24}
                shape="rectangle"
              />
              <SkeletonItem
                className={classes.pill}
                size={24}
                shape="rectangle"
              />
              <SkeletonItem
                className={classes.pill}
                size={24}
                shape="rectangle"
              />
            </div>
          </div>

          <div className={classes.section}>
            <SkeletonItem className={classes.fieldBlock} size={20} />
            <SkeletonItem className={classes.smallField} size={32} />
            <SkeletonItem className={classes.fieldBlock} size={20} />
            <SkeletonItem className={classes.smallField} size={32} />
          </div>

          <SkeletonItem
            className={mergeClasses(classes.card, classes.largeCard)}
            size={40}
            shape="rectangle"
          />
          <SkeletonItem className={classes.card} size={40} shape="rectangle" />
        </Skeleton>
      </div>
    </div>
  );
};
